import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  InboxQuery,
  NotificationCategory,
  NotificationInbox,
  NotificationPreferences,
  UpdatePreferencesInput,
} from '@nest-recruit/shared';
import { apiGet, apiPost, ApiError } from '../../../lib/api-client';

/**
 * NOTIF-01 and NOTIF-02.
 *
 * **One prefix for everything notification-shaped**, because that is the key the
 * server pushes: the gateway sends `{ type: 'invalidate', keys: ['notifications'] }`
 * and TanStack Query refetches the inbox, the unread count and the bell together. A
 * bell showing three unread beside a list showing none is exactly the sort of
 * half-refreshed screen that makes somebody stop trusting it.
 */
export const NOTIFICATIONS_KEY = ['notifications'] as const;

export interface InboxParams {
  readonly filter: 'all' | 'unread';
  readonly category?: NotificationCategory;
  readonly page?: number;
}

export function useInbox(params: InboxParams): UseQueryResult<NotificationInbox, ApiError> {
  return useQuery<NotificationInbox, ApiError>({
    queryKey: [...NOTIFICATIONS_KEY, 'inbox', params.filter, params.category ?? null, params.page ?? 1],
    queryFn: () =>
      apiGet<NotificationInbox>('/notifications', {
        query: {
          filter: params.filter,
          ...(params.category ? { category: params.category } : {}),
          page: params.page ?? 1,
        },
      }),
    // Changing a filter swaps the rows in place rather than dropping to a skeleton
    // (CLAUDE.md §5a rule 2).
    placeholderData: (previous) => previous,
  });
}

/**
 * What the bell in the shell reads.
 *
 * A short page rather than the whole inbox: the panel shows the most recent few and
 * "see all" goes to NOTIF-01. Asking for two hundred rows to render eight is work
 * done on every push, for every signed-in tab.
 */
export function useNotificationBell(): UseQueryResult<NotificationInbox, ApiError> {
  return useQuery<NotificationInbox, ApiError>({
    queryKey: [...NOTIFICATIONS_KEY, 'bell'],
    queryFn: () =>
      apiGet<NotificationInbox>('/notifications', {
        query: { filter: 'all', pageSize: BELL_PAGE_SIZE } satisfies Partial<
          Record<keyof InboxQuery, string | number>
        >,
      }),
    placeholderData: (previous) => previous,
    // The socket is the freshness mechanism. This is the backstop for a tab whose
    // socket never connected, not the primary path — hence minutes, not seconds.
    refetchInterval: 5 * 60_000,
  });
}

const BELL_PAGE_SIZE = 8;

/**
 * Marking read, optimistically.
 *
 * The user can already see the unread markers, so they must never watch them
 * disappear via a spinner (CLAUDE.md §5a). The cache is rewritten first and the
 * pending state is held until the refetch lands — releasing it earlier re-renders
 * from the stale cache and the badge visibly jumps back and then forward again.
 */
/** What `onMutate` hands `onError` so a failed write can be rolled back exactly. */
interface MarkReadContext {
  readonly snapshot: readonly [readonly unknown[], NotificationInbox | undefined][];
}

export function useMarkRead() {
  const queryClient = useQueryClient();

  return useMutation<{ unreadCount: number }, ApiError, { ids?: readonly string[] }, MarkReadContext>({
    mutationFn: (input) => apiPost<{ unreadCount: number }>('/notifications/read', input),
    onMutate: async (input): Promise<MarkReadContext> => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_KEY });
      const snapshot = queryClient.getQueriesData<NotificationInbox>({
        queryKey: NOTIFICATIONS_KEY,
      });
      const readAt = new Date().toISOString();

      queryClient.setQueriesData<NotificationInbox>({ queryKey: NOTIFICATIONS_KEY }, (current) => {
        if (!current) return current;
        const shouldMark = (id: string): boolean => !input.ids || input.ids.includes(id);
        const marked = current.rows.filter(
          (row) => row.readAt === null && shouldMark(row.id),
        ).length;

        return {
          ...current,
          rows: current.rows.map((row) =>
            row.readAt === null && shouldMark(row.id) ? { ...row, readAt } : row,
          ),
          // `unreadCount` counts the whole inbox, not the page on screen — so
          // "mark all" is zero and "mark these" subtracts only what actually moved.
          unreadCount: input.ids ? Math.max(0, current.unreadCount - marked) : 0,
        };
      });

      return { snapshot };
    },
    onError: (_error, _input, context) => {
      for (const [key, value] of context?.snapshot ?? []) queryClient.setQueryData(key, value);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    },
  });
}

// --- NOTIF-02 ----------------------------------------------------------------

export const PREFERENCES_KEY = [...NOTIFICATIONS_KEY, 'preferences'] as const;

export function usePreferences(): UseQueryResult<NotificationPreferences, ApiError> {
  return useQuery<NotificationPreferences, ApiError>({
    queryKey: PREFERENCES_KEY,
    queryFn: () => apiGet<NotificationPreferences>('/notifications/preferences'),
    staleTime: 60_000,
  });
}

interface PreferencesContext {
  readonly previous: NotificationPreferences | undefined;
}

export function useUpdatePreferences() {
  const queryClient = useQueryClient();

  return useMutation<
    NotificationPreferences,
    ApiError,
    UpdatePreferencesInput,
    PreferencesContext
  >({
    mutationFn: (input) => apiPost<NotificationPreferences>('/notifications/preferences', input),
    // The switches flip the instant they are clicked and stay flipped; the server's
    // answer replaces the cache when it lands.
    onMutate: async (input): Promise<PreferencesContext> => {
      await queryClient.cancelQueries({ queryKey: PREFERENCES_KEY });
      const previous = queryClient.getQueryData<NotificationPreferences>(PREFERENCES_KEY);
      queryClient.setQueryData<NotificationPreferences>(PREFERENCES_KEY, {
        categories: input.categories,
        isDigestEnabled: input.isDigestEnabled,
      });
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(PREFERENCES_KEY, context.previous);
    },
    onSuccess: (saved) => {
      queryClient.setQueryData(PREFERENCES_KEY, saved);
    },
  });
}

// --- The delivery log ---------------------------------------------------------------------------------------

/**
 * ADMIN Email delivery's key. Its hooks live with the page (`features/admin/api/email-delivery-api.ts`); the test send
 * below refreshes it, so the test email appears in the log at once.
 */
export const MESSAGE_LOG_KEY = ['message-log'] as const;

export interface TestNotificationResult {
  readonly recipientUserId: string;
  readonly channels: readonly string[];
  readonly escalatesIn: number | null;
}

export function useSendTestNotification() {
  const queryClient = useQueryClient();

  return useMutation<
    TestNotificationResult,
    ApiError,
    { recipientUserId?: string; escalateAfterSeconds?: number }
  >({
    mutationFn: (input) => apiPost<TestNotificationResult>('/notifications/test', input),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: MESSAGE_LOG_KEY }),
        queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY }),
      ]);
    },
  });
}
